import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  ErrorState,
  LoadingSkeleton,
} from '@berky/ui'
import { useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'

import { useFeedbackApi } from './feedback-api'

interface Attachment {
  fileName: string
  contentType: string
  data: string
}

export function FeedbackAttachmentView({
  feedbackId,
  hasAttachment,
}: {
  feedbackId: string
  hasAttachment: boolean
}) {
  const api = useFeedbackApi()
  const query = useQuery({
    queryKey: ['feedback', 'eintraege', 'attachment', feedbackId],
    queryFn: ({ signal }) =>
      api.get<Attachment>(`/eintraege/${feedbackId}/anhang`, { signal }),
    enabled: hasAttachment,
  })
  if (!hasAttachment)
    return (
      <p className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
        Dieses Feedback wurde ohne Dateianhang eingereicht.
      </p>
    )
  if (query.isPending) return <LoadingSkeleton variant="form" rows={3} />
  if (query.isError)
    return (
      <ErrorState
        description="Der Dateianhang konnte nicht geladen werden."
        onRetry={() => void query.refetch()}
      />
    )
  const file = query.data
  const isImage = ['image/png', 'image/jpeg', 'image/webp'].includes(file.contentType)
  const imageSource = `data:${file.contentType};base64,${file.data}`

  function download() {
    const binary = atob(file.data)
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0))
    const url = URL.createObjectURL(
      new Blob([bytes], { type: 'application/octet-stream' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = file.fileName
    link.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  return (
    <div className="space-y-3 rounded-lg border bg-card p-3">
      {isImage && (
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              className="h-auto w-full cursor-zoom-in p-0"
              aria-label="Dateianhang vergrößern"
            >
              <img src={imageSource} alt={file.fileName} className="h-auto max-w-full" />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90svh] overflow-auto sm:max-w-6xl">
            <DialogHeader>
              <DialogTitle>{file.fileName}</DialogTitle>
            </DialogHeader>
            <img
              src={imageSource}
              alt={file.fileName}
              className="max-h-[80svh] max-w-full object-contain"
            />
          </DialogContent>
        </Dialog>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 break-all text-sm">{file.fileName}</span>
        <Button variant="outline" size="sm" onClick={download}>
          <Download aria-hidden="true" />
          Herunterladen
        </Button>
      </div>
    </div>
  )
}
